import { useEffect, useState } from 'react';
import type { ChangeEvent } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Container,
  Typography,
  Alert,
  CircularProgress,
  Button,
  Avatar,
  useTheme,
  useMediaQuery,
  Breadcrumbs,
  Link,
  TextField,
  Grid,
  Divider,
} from '@mui/material';
import {
  AccessTime as AccessTimeIcon,
  AttachMoney as AttachMoneyIcon,
  NavigateNext as NavigateNextIcon,
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
} from '@mui/icons-material';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase';
import type { Service, Tenant } from '../../lib/db/types';

interface LocationState {
  tenant: Tenant & { id: string };
  service: Service & { id: string };
  provider: { name: string; photoUrl?: string };
  providerId: string;
  selectedDate: string;
  selectedTime: string;
  selectedEndTime: string;
  reschedulePublicAccessHash?: string;
}

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  notes: string;
}

interface FormErrors {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
}

export function BookingContactPage() {
  const { tenantSlug, serviceId, providerId } = useParams<{
    tenantSlug: string;
    serviceId: string;
    providerId: string;
  }>();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const state = location.state as LocationState | null;

  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    notes: '',
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [bookingComplete, setBookingComplete] = useState(false);
  const [appointmentHash, setAppointmentHash] = useState<string>('');

  // Redirect if no state (user refreshed or came directly)
  useEffect(() => {
    if (!state) {
      // Redirect back to time selection if no state
      navigate(`/book/${tenantSlug}/service/${serviceId}/provider/${providerId}`, {
        replace: true,
      });
    }
  }, [state, navigate, tenantSlug, serviceId, providerId]);

  if (!state) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          bgcolor: 'grey.50',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const { tenant, service, provider, selectedDate, selectedTime, selectedEndTime } = state;

  const formatDuration = (minutes: number): string => {
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    if (remainingMinutes === 0) return `${hours} hr`;
    return `${hours} hr ${remainingMinutes} min`;
  };

  const formatPrice = (price?: number, currency?: string): string => {
    if (price === undefined || price === null) return 'Free';
    const dollars = price / 100;
    const currencySymbol = currency === 'EUR' ? '€' : '$';
    return `${currencySymbol}${dollars.toFixed(2)}`;
  };

  const formatTime = (timeStr: string): string => {
    const [hours, minutes] = timeStr.split(':').map(Number);
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
  };

  const formatDate = (dateStr: string): string => {
    return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }

    if (formData.phone && !/^\+?[\d\s-()]{10,}$/.test(formData.phone)) {
      newErrors.phone = 'Please enter a valid phone number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof FormData) => (
    e: ChangeEvent<HTMLInputElement>
  ) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
    // Clear error when user types
    if (errors[field as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleSubmit = async () => {
    if (!state.reschedulePublicAccessHash && !validateForm()) return;

    setSubmitting(true);
    setSubmitError(null);

    try {
      const startDateTime = new Date(`${selectedDate}T${selectedTime}`);

      const result = state.reschedulePublicAccessHash
        ? await httpsCallable(functions, 'rescheduleAppointment')({
            publicAccessHash: state.reschedulePublicAccessHash,
            newStartTime: startDateTime.toISOString(),
          })
        : await httpsCallable(functions, 'bookAppointment')({
            tenantSlug,
            serviceId: service.id,
            providerId: providerId,
            startTime: startDateTime.toISOString(),
            customerType: 'guest',
            guestInfo: {
              firstName: formData.firstName,
              lastName: formData.lastName,
              email: formData.email,
              phone: formData.phone || undefined,
            },
            source: 'public_portal',
            customerNotes: formData.notes,
          });

      const data = result.data as {
        appointmentId: string;
        publicAccessHash?: string;
        status: string;
        checkoutUrl?: string;
      };

      setAppointmentHash(data.publicAccessHash || state.reschedulePublicAccessHash || '');
      if (data.checkoutUrl) {
        window.location.assign(data.checkoutUrl);
        return;
      }
      setBookingComplete(true);
    } catch (err: any) {
      console.error('Error booking appointment:', err);
      setSubmitError(err.message || 'Failed to book appointment. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleBackToTime = () => {
    navigate(`/book/${tenantSlug}/service/${serviceId}/provider/${providerId}`);
  };

  const handleBackToServices = () => {
    navigate(`/book/${tenantSlug}`);
  };

  const primaryColor = tenant?.branding?.primaryColor || theme.palette.primary.main;

  if (bookingComplete) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: 'grey.50',
          pb: 4,
        }}
      >
        <Box
          sx={{
            bgcolor: 'white',
            borderBottom: '1px solid',
            borderColor: 'divider',
            py: { xs: 2, sm: 3 },
            px: { xs: 2, sm: 3 },
            mb: { xs: 2, sm: 4 },
          }}
        >
          <Container maxWidth="lg">
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                flexDirection: { xs: 'column', sm: 'row' },
                textAlign: { xs: 'center', sm: 'left' },
              }}
            >
              {tenant.branding?.logoUrl ? (
                <Box
                  component="img"
                  src={tenant.branding.logoUrl}
                  alt={tenant.name}
                  sx={{
                    width: { xs: 48, sm: 56 },
                    height: { xs: 48, sm: 56 },
                    objectFit: 'contain',
                    borderRadius: 1,
                  }}
                />
              ) : (
                <Box
                  sx={{
                    width: { xs: 48, sm: 56 },
                    height: { xs: 48, sm: 56 },
                    borderRadius: 1,
                    bgcolor: primaryColor,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: { xs: '1.5rem', sm: '2rem' },
                    fontWeight: 'bold',
                  }}
                >
                  {tenant.name.charAt(0).toUpperCase()}
                </Box>
              )}
              <Box>
                <Typography
                  variant="h5"
                  component="h1"
                  fontWeight={600}
                  sx={{ color: 'text.primary' }}
                >
                  {tenant.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Book an appointment
                </Typography>
              </Box>
            </Box>
          </Container>
        </Box>

        <Container maxWidth="sm">
          <Card sx={{ textAlign: 'center', py: { xs: 4, sm: 6 } }}
          >
            <CardContent>
              <CheckCircleIcon
                sx={{ fontSize: 64, color: 'success.main', mb: 2 }}
              />
              <Typography variant="h5" gutterBottom fontWeight={600}>
                {state.reschedulePublicAccessHash ? 'Appointment Rescheduled!' : 'Booking Confirmed!'}
              </Typography>
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ mb: 3 }}
              >
                {state.reschedulePublicAccessHash
                  ? 'Your appointment has been successfully rescheduled.'
                  : `Your appointment has been successfully booked. A confirmation email has been sent to ${formData.email}.`}
              </Typography>

              <Box
                sx={{
                  bgcolor: 'grey.50',
                  p: 3,
                  borderRadius: 2,
                  mb: 3,
                  textAlign: 'left',
                }}
              >
                <Typography variant="subtitle2" gutterBottom>
                  Appointment Details
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {service.name} with {provider.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {formatDate(selectedDate)} at {formatTime(selectedTime)}
                </Typography>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  gap: 2,
                  justifyContent: 'center',
                  flexDirection: { xs: 'column', sm: 'row' },
                }}
              >
                <Button
                  variant="contained"
                  href={`/appointment/${appointmentHash}`}
                  sx={{
                    bgcolor: primaryColor,
                    '&:hover': { bgcolor: primaryColor, filter: 'brightness(0.9)' },
                  }}
                >
                  Manage Appointment
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => navigate(`/book/${tenantSlug}`)}
                >
                  Book Another
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Container>

        <Box
          sx={{
            mt: 6,
            py: 3,
            textAlign: 'center',
            borderTop: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Container maxWidth="lg">
            <Typography variant="body2" color="text.secondary">
              Powered by{' '}
              <Box component="span" sx={{ fontWeight: 700 }}>
                FIT
              </Box>
              <Box component="span" sx={{ fontStyle: 'italic' }}>
                Stac
              </Box>
            </Typography>
          </Container>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'grey.50',
        pb: 4,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          bgcolor: 'white',
          borderBottom: '1px solid',
          borderColor: 'divider',
          py: { xs: 2, sm: 3 },
          px: { xs: 2, sm: 3 },
          mb: { xs: 2, sm: 4 },
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              flexDirection: { xs: 'column', sm: 'row' },
              textAlign: { xs: 'center', sm: 'left' },
            }}
          >
            {tenant.branding?.logoUrl ? (
              <Box
                component="img"
                src={tenant.branding.logoUrl}
                alt={tenant.name}
                sx={{
                  width: { xs: 48, sm: 56 },
                  height: { xs: 48, sm: 56 },
                  objectFit: 'contain',
                  borderRadius: 1,
                }}
              />
            ) : (
              <Box
                sx={{
                  width: { xs: 48, sm: 56 },
                  height: { xs: 48, sm: 56 },
                  borderRadius: 1,
                  bgcolor: primaryColor,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: { xs: '1.5rem', sm: '2rem' },
                  fontWeight: 'bold',
                }}
              >
                {tenant.name.charAt(0).toUpperCase()}
              </Box>
            )}
            <Box>
              <Typography
                variant="h5"
                component="h1"
                fontWeight={600}
                sx={{ color: 'text.primary' }}
              >
                {tenant.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Book an appointment
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg">
        {/* Breadcrumbs */}
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          sx={{ mb: 2 }}
        >
          <Link
            component="button"
            variant="body2"
            onClick={handleBackToServices}
            sx={{
              color: 'text.secondary',
              textDecoration: 'none',
              '&:hover': { color: primaryColor },
            }}
          >
            Services
          </Link>
          <Link
            component="button"
            variant="body2"
            onClick={handleBackToTime}
            sx={{
              color: 'text.secondary',
              textDecoration: 'none',
              '&:hover': { color: primaryColor },
            }}
          >
            Time
          </Link>
          <Typography variant="body2" color="text.primary">
            Your Details
          </Typography>
        </Breadcrumbs>

        {/* Error Alert */}
        {submitError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {submitError}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Left Column - Booking Summary */}
          <Grid item xs={12} md={5}>
            <Card sx={{ position: { md: 'sticky' }, top: 24 }}>
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Typography
                  variant="h6"
                  component="h2"
                  fontWeight={600}
                  sx={{ mb: 3 }}
                >
                  Booking Summary
                </Typography>

                {/* Service Info */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="caption" color="text.secondary">
                    Service
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {service.name}
                  </Typography>
                  {service.description && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {service.description}
                    </Typography>
                  )}
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Provider Info */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    mb: 3,
                  }}
                >
                  <Avatar
                    src={provider.photoUrl}
                    alt={provider.name}
                    sx={{
                      width: 48,
                      height: 48,
                      bgcolor: primaryColor,
                    }}
                  >
                    {provider.name.charAt(0).toUpperCase()}
                  </Avatar>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Provider
                    </Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {provider.name}
                    </Typography>
                  </Box>
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Date & Time */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    Date & Time
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {formatDate(selectedDate)}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {formatTime(selectedTime)} - {formatTime(selectedEndTime)}
                  </Typography>
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Duration & Price */}
                <Box
                  sx={{
                    display: 'flex',
                    gap: 3,
                    flexWrap: 'wrap',
                  }}
                >
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Duration
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                      }}
                    >
                      <AccessTimeIcon fontSize="small" color="action" />
                      <Typography variant="body1" fontWeight={500}>
                        {formatDuration(service.duration)}
                      </Typography>
                    </Box>
                  </Box>

                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Price
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                      }}
                    >
                      <AttachMoneyIcon fontSize="small" color="action" />
                      <Typography variant="body1" fontWeight={500}>
                        {formatPrice(service.price, service.currency)}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Right Column - Contact Form */}
          <Grid item xs={12} md={7}>
            <Card>
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Typography
                  variant="h6"
                  component="h2"
                  fontWeight={600}
                  sx={{ mb: 1 }}
                >
                  Your Information
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Please provide your contact details to complete the booking.
                </Typography>

                <Grid container spacing={2}>
                  {/* First Name */}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="First Name"
                      value={formData.firstName}
                      onChange={handleInputChange('firstName')}
                      error={!!errors.firstName}
                      helperText={errors.firstName}
                      required
                      InputProps={{
                        startAdornment: (
                          <PersonIcon
                            fontSize="small"
                            sx={{ mr: 1, color: 'text.secondary' }}
                          />
                        ),
                      }}
                    />
                  </Grid>

                  {/* Last Name */}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Last Name"
                      value={formData.lastName}
                      onChange={handleInputChange('lastName')}
                      error={!!errors.lastName}
                      helperText={errors.lastName}
                      required
                      InputProps={{
                        startAdornment: (
                          <PersonIcon
                            fontSize="small"
                            sx={{ mr: 1, color: 'text.secondary' }}
                          />
                        ),
                      }}
                    />
                  </Grid>

                  {/* Email */}
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange('email')}
                      error={!!errors.email}
                      helperText={errors.email}
                      required
                      InputProps={{
                        startAdornment: (
                          <EmailIcon
                            fontSize="small"
                            sx={{ mr: 1, color: 'text.secondary' }}
                          />
                        ),
                      }}
                    />
                  </Grid>

                  {/* Phone */}
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Phone (optional)"
                      type="tel"
                      value={formData.phone}
                      onChange={handleInputChange('phone')}
                      error={!!errors.phone}
                      helperText={errors.phone}
                      InputProps={{
                        startAdornment: (
                          <PhoneIcon
                            fontSize="small"
                            sx={{ mr: 1, color: 'text.secondary' }}
                          />
                        ),
                      }}
                    />
                  </Grid>

                  {/* Notes */}
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Notes (optional)"
                      multiline
                      rows={3}
                      value={formData.notes}
                      onChange={handleInputChange('notes')}
                      placeholder="Any special requests or information for your provider..."
                    />
                  </Grid>
                </Grid>

                <Divider sx={{ my: 3 }} />

                {/* Action Buttons */}
                <Box
                  sx={{
                    display: 'flex',
                    gap: 2,
                    flexDirection: { xs: 'column-reverse', sm: 'row' },
                  }}
                >
                  <Button
                    variant="outlined"
                    startIcon={<ArrowBackIcon />}
                    onClick={handleBackToTime}
                    disabled={submitting}
                    fullWidth={isMobile}
                  >
                    Back
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={submitting}
                    startIcon={
                      submitting ? <CircularProgress size={20} /> : <CheckCircleIcon />
                    }
                    fullWidth={isMobile}
                    sx={{
                      bgcolor: primaryColor,
                      '&:hover': {
                        bgcolor: primaryColor,
                        filter: 'brightness(0.9)',
                      },
                    }}
                  >
                    {submitting ? 'Booking...' : 'Confirm Booking'}
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>

      {/* Footer */}
      <Box
        sx={{
          mt: 6,
          py: 3,
          textAlign: 'center',
          borderTop: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="body2" color="text.secondary">
            Powered by{' '}
            <Box component="span" sx={{ fontWeight: 700 }}>
              FIT
            </Box>
            <Box component="span" sx={{ fontStyle: 'italic' }}>
              Stac
            </Box>
          </Typography>
        </Container>
      </Box>
    </Box>
  );
}

export default BookingContactPage;
